import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Lock as PrivateIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { ResearchNote, ResearchType, ResearchStatus, Priority, User } from '../../types';
import { formatDistanceToNow } from 'date-fns';

interface ResearchViewerProps {
  researchNote: ResearchNote;
  currentUser: User;
  open: boolean;
  onClose: () => void;
  onEdit: (note: ResearchNote) => void;
  onDelete: (noteId: string) => void;
  onApprove: (noteId: string) => void;
  onReject: (noteId: string, reason: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const ResearchViewer: React.FC<ResearchViewerProps> = ({
  researchNote,
  currentUser,
  open,
  onClose,
  onEdit,
  onDelete,
  onApprove,
  onReject,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // Format research type for display
  const formatType = (type: ResearchType): string => {
    return type
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format status for display
  const formatStatus = (status: ResearchStatus): string => {
    return status
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get status color
  const getStatusColor = (status: ResearchStatus): 'success' | 'info' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'info';
      case 'on-hold':
        return 'warning';
      case 'blocked':
        return 'warning';
      case 'not-started':
      default:
        return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: Priority): 'error' | 'warning' | 'info' => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'info';
    }
  };

  // Get approval status color
  const getApprovalStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Check permissions
  const canEdit = currentUser.role === 'admin' ||
                  researchNote.createdBy === currentUser.uid ||
                  researchNote.assignedTo.includes(currentUser.uid);
  const canDelete = currentUser.role === 'admin' || researchNote.createdBy === currentUser.uid;
  const canModerate = currentUser.role === 'admin';

  // Handle delete
  const handleDelete = useCallback(() => {
    onDelete(researchNote.id!);
    onClose();
  }, [researchNote, onDelete, onClose]);

  // Handle approve
  const handleApprove = useCallback(() => {
    onApprove(researchNote.id!);
  }, [researchNote, onApprove]);

  // Handle reject
  const handleReject = useCallback(() => {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason !== null) {
      onReject(researchNote.id!, reason);
    }
  }, [researchNote, onReject]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography variant="h5" component="div" gutterBottom>
              {researchNote.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={formatStatus(researchNote.status)}
                size="small"
                color={getStatusColor(researchNote.status)}
              />
              <Chip
                label={researchNote.priority}
                size="small"
                color={getPriorityColor(researchNote.priority)}
                variant="outlined"
              />
              <Chip
                label={formatType(researchNote.type)}
                size="small"
                variant="outlined"
              />
              {researchNote.isPrivate ? (
                <Chip icon={<PrivateIcon />} label="Private" size="small" variant="outlined" />
              ) : (
                <>
                  <Chip icon={<PublicIcon />} label="Shared" size="small" color="primary" variant="outlined" />
                  {researchNote.approvalStatus && (
                    <Chip
                      label={researchNote.approvalStatus}
                      size="small"
                      color={getApprovalStatusColor(researchNote.approvalStatus)}
                    />
                  )}
                </>
              )}
            </Stack>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Stack spacing={3}>
          {/* Progress bar */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Research Progress
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {researchNote.completionPercentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={researchNote.completionPercentage}
              color={researchNote.completionPercentage === 100 ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Paper>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Details" />
            <Tab label={`Tasks (${researchNote.tasks.length})`} />
            <Tab label={`Sources (${researchNote.sources.length})`} />
            <Tab label="History" />
          </Tabs>

          {/* Details Tab */}
          <TabPanel value={activeTab} index={0}>
            <Stack spacing={3}>
              {/* Objective */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Research Objective
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {researchNote.objective}
                </Typography>
              </Box>

              {/* Findings */}
              {researchNote.findings && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Findings
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'action.hover' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {researchNote.findings}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Stack>
          </TabPanel>

          {/* Tasks Tab */}
          <TabPanel value={activeTab} index={1}>
            {researchNote.tasks.length === 0 ? (
              <Alert severity="info">No tasks have been added to this research note.</Alert>
            ) : (
              <Paper variant="outlined">
                <List>
                  {researchNote.tasks.map((task, index) => (
                    <ListItem key={task.id} divider={index < researchNote.tasks.length - 1}>
                      <ListItemIcon>
                        <Checkbox edge="start" checked={task.isCompleted} disabled />
                      </ListItemIcon>
                      <ListItemText
                        primary={task.description}
                        secondary={
                          task.isCompleted && task.completedAt
                            ? `Completed ${formatDistanceToNow(new Date(task.completedAt))} ago`
                            : undefined
                        }
                        sx={{
                          textDecoration: task.isCompleted ? 'line-through' : 'none',
                          opacity: task.isCompleted ? 0.6 : 1,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ p: 2, backgroundColor: 'action.hover' }}>
                  <Typography variant="body2" color="text.secondary">
                    {researchNote.tasks.filter(t => t.isCompleted).length} of {researchNote.tasks.length} tasks completed
                  </Typography>
                </Box>
              </Paper>
            )}
          </TabPanel>

          {/* Sources Tab */}
          <TabPanel value={activeTab} index={2}>
            {researchNote.sources.length === 0 ? (
              <Alert severity="info">No sources have been cited for this research note.</Alert>
            ) : (
              <Stack spacing={2}>
                {researchNote.sources.map((source, index) => (
                  <Paper key={source.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        [{index + 1}]
                      </Typography>
                      <Chip label={source.sourceType} size="small" />
                    </Stack>

                    <Typography variant="subtitle2" gutterBottom>
                      {source.title}
                    </Typography>

                    {source.author && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Author: {source.author}
                      </Typography>
                    )}

                    {source.publisher && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Publisher: {source.publisher}
                      </Typography>
                    )}

                    {source.url && (
                      <Typography variant="body2" color="primary" gutterBottom>
                        <a href={source.url} target="_blank" rel="noopener noreferrer">
                          {source.url}
                        </a>
                      </Typography>
                    )}

                    {source.formattedCitation && (
                      <Paper variant="outlined" sx={{ p: 1, mt: 1, backgroundColor: 'action.hover' }}>
                        <Typography variant="caption">
                          {source.formattedCitation}
                        </Typography>
                      </Paper>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </TabPanel>

          {/* History Tab */}
          <TabPanel value={activeTab} index={3}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created By
                </Typography>
                <Typography variant="body1">{researchNote.researcherName || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(researchNote.createdAt))} ago
                </Typography>
              </Box>

              {researchNote.updatedAt && researchNote.updatedAt !== researchNote.createdAt && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">{researchNote.lastUpdatedByName || 'Unknown'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(researchNote.updatedAt))} ago
                  </Typography>
                </Box>
              )}

              {researchNote.lastActivityAt && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Activity
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(researchNote.lastActivityAt))} ago
                  </Typography>
                </Box>
              )}

              {researchNote.approvalStatus === 'approved' && researchNote.approvedBy && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Approved By
                  </Typography>
                  <Typography variant="body1">{researchNote.approvedBy || 'Unknown'}</Typography>
                  {researchNote.approvedAt && (
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(researchNote.approvedAt))} ago
                    </Typography>
                  )}
                </Box>
              )}

              {researchNote.approvalStatus === 'rejected' && researchNote.rejectionReason && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Rejection Reason
                  </Typography>
                  <Alert severity="warning">{researchNote.rejectionReason}</Alert>
                </Box>
              )}
            </Stack>
          </TabPanel>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box>
          {/* Moderation actions (Admin only, shared notes only) */}
          {canModerate && !researchNote.isPrivate && researchNote.approvalStatus === 'pending' && (
            <>
              <Button startIcon={<ApproveIcon />} color="success" onClick={handleApprove} sx={{ mr: 1 }}>
                Approve
              </Button>
              <Button startIcon={<RejectIcon />} color="error" onClick={handleReject}>
                Reject
              </Button>
            </>
          )}

          {/* Delete action */}
          {canDelete && (
            <Button startIcon={<DeleteIcon />} color="error" onClick={handleDelete}>
              Delete
            </Button>
          )}
        </Box>

        <Box>
          {/* Edit action */}
          {canEdit && (
            <Button startIcon={<EditIcon />} onClick={() => onEdit(researchNote)} sx={{ mr: 1 }}>
              Edit
            </Button>
          )}

          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ResearchViewer;
